/**
 * The adaptive-music intensity state machine (audio-direction §4.3), fed at 4 Hz from game state:
 * Calm (0) → Tension (1) → Combat (2) → Climax (3: nothing triggers it in M1; M3's relay reboot floors it). The state rises at once
 * to whatever the game asks for; it falls only after quiet: Combat → Tension once nothing has asked for Combat
 * for 8 s, and Tension → Calm once nothing has asked for Tension (or more) for 20 s. The conductor applies each
 * change on the next bar. `force` pins a level (the audition page); `down` is the death state (a low drone
 * until respawn). Pure and allocation-free.
 */
import type { MusicConfig } from './musicSchema'

export const LEVEL = Object.freeze({ calm: 0, tension: 1, combat: 2, climax: 3 })
export type Level = (typeof LEVEL)[keyof typeof LEVEL]
export const LEVEL_NAMES = ['Calm', 'Tension', 'Combat', 'Climax'] as const

export interface IntensityState {
  level: Level
  /** Seconds since the game last asked for Combat or more, and for Tension or more. */
  sinceCombat: number
  sinceTension: number
  /** Pinned level (-1 = none). */
  forced: -1 | Level
  /** The player is dead: the score is down to its drone until `recover`. */
  down: boolean
}

export function createIntensityState(): IntensityState {
  return { level: LEVEL.calm, sinceCombat: Infinity, sinceTension: Infinity, forced: -1, down: false }
}

/**
 * Advances by `dt` seconds with the level the game asks for. Returns true when the level changed. `floor`
 * (additive, M3 R11: Tension during an interdiction, Climax during a relay reboot) is the least the game asks for:
 * the machine runs on `max(desired, floor)`.
 */
export function stepIntensity(state: IntensityState, desired: Level, dt: number, cfg: MusicConfig['intensity'], floor: Level = LEVEL.calm): boolean {
  const before = state.level
  if (floor > desired) desired = floor
  if (desired >= LEVEL.combat) state.sinceCombat = 0
  else state.sinceCombat += dt
  if (desired >= LEVEL.tension) state.sinceTension = 0
  else state.sinceTension += dt
  const forced = state.forced
  if (forced !== -1) {
    state.level = forced
    return state.level !== before
  }
  let level: Level = state.level
  if (desired > level) level = desired
  if (level === LEVEL.climax && desired < LEVEL.climax && state.sinceCombat >= cfg.fallCombatSeconds) level = LEVEL.combat
  if (level === LEVEL.combat && desired < LEVEL.combat && state.sinceCombat >= cfg.fallCombatSeconds) level = LEVEL.tension
  if (level === LEVEL.tension && desired < LEVEL.tension && state.sinceTension >= cfg.fallTensionSeconds) level = LEVEL.calm
  state.level = level
  return level !== before
}

export function forceIntensity(state: IntensityState, level: -1 | Level): void {
  state.forced = level
  if (level !== -1) state.level = level
}

/** What the game situation asks for (see `IntensityInput`). */
export interface IntensityInput {
  /** Distance (m) to the nearest living raider (Infinity when none). */
  nearestHostile: number
  /** Seconds since the player was last hit and last fired. */
  sinceHit: number
  sinceFired: number
}

export function desiredLevel(input: IntensityInput, cfg: MusicConfig['intensity']): Level {
  const near = input.nearestHostile
  if (near < cfg.combatRange) return LEVEL.combat
  if (input.sinceHit < cfg.engageSeconds) return LEVEL.combat
  if (near < cfg.tensionRange && input.sinceFired < cfg.engageSeconds) return LEVEL.combat
  if (near < cfg.tensionRange) return LEVEL.tension
  return LEVEL.calm
}
